import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Cookie, ShieldCheck, BarChart3, Sparkles } from "lucide-react";
import {
  acceptAll,
  rejectAll,
  savePreferences,
  useConsent,
} from "@/lib/consent";
import {
  loadGoogleFonts,
  unloadGoogleFonts,
} from "@/lib/fonts";
import {
  initConsentMode,
  loadAnalytics,
  unloadAnalytics,
} from "@/lib/analytics";
import { useT } from "@/lib/i18n";

/**
 * DSGVO/GDPR cookie consent banner + granular preferences dialog.
 * Renders a fixed bottom banner on first visit, a modal for detailed choices,
 * and side-effect-loads/unloads Google Fonts + Google Analytics based on the
 * current consent state.
 */
export default function CookieConsent() {
  const { t } = useT();
  const { consent } = useConsent();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Local (draft) prefs for the dialog
  const [draftFn, setDraftFn] = useState(consent.functional);
  const [draftSt, setDraftSt] = useState(consent.statistics);

  // Init Consent Mode v2 once on mount
  useEffect(() => {
    initConsentMode();
  }, []);

  // React to consent changes: load/unload third-party stuff
  useEffect(() => {
    if (consent.functional) loadGoogleFonts();
    else unloadGoogleFonts();

    if (consent.statistics) loadAnalytics();
    else unloadAnalytics();
  }, [consent.functional, consent.statistics]);

  // Sync draft to current on dialog open
  useEffect(() => {
    if (dialogOpen) {
      setDraftFn(consent.functional);
      setDraftSt(consent.statistics);
    }
  }, [dialogOpen, consent.functional, consent.statistics]);

  // Listen for "open preferences" events from anywhere in the app
  useEffect(() => {
    const handler = () => setDialogOpen(true);
    window.addEventListener("cookie-consent:open", handler);
    return () => window.removeEventListener("cookie-consent:open", handler);
  }, []);

  const showBanner = consent.status !== "granted";

  // Block body scroll while the persistent banner is visible, so users cannot
  // scroll the page underneath and pretend the notice does not exist.
  useEffect(() => {
    if (showBanner) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [showBanner]);

  const bannerAcceptAll = () => acceptAll();
  const bannerRejectAll = () => rejectAll();
  const bannerCustomize = () => setDialogOpen(true);

  const dialogSave = () => {
    savePreferences({ functional: draftFn, statistics: draftSt });
    setDialogOpen(false);
  };

  return (
    <>
      {showBanner && (
        <>
          {/* Persistent full-screen backdrop — blocks all interaction with the
              page underneath. The banner cannot be dismissed by clicking away.
              User MUST make an explicit choice (accept / reject / customize). */}
          <div
            aria-hidden="true"
            data-testid="cookie-backdrop"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: 9998,
              backgroundColor: "rgba(0, 0, 0, 0.72)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
          />

          <div
            role="alertdialog"
            aria-modal="true"
            aria-label={t("consent.banner.aria")}
            aria-describedby="cookie-banner-body"
            data-testid="cookie-banner"
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              padding: "12px",
            }}
          >
          <div className="mx-auto max-w-4xl bg-[#131620] border border-[#272A35] rounded-xl shadow-2xl p-5 sm:p-6 ring-1 ring-[#D4AF37]/20">
            <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
              <div className="hidden sm:flex h-10 w-10 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 items-center justify-center shrink-0">
                <Cookie className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif-display text-lg text-white mb-1.5 tracking-tight">
                  {t("consent.banner.title")}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {t("consent.banner.body")}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  <a
                    href="/datenschutz"
                    className="underline decoration-dotted underline-offset-2 hover:text-slate-300"
                    data-testid="banner-link-privacy"
                  >
                    {t("consent.link.privacy")}
                  </a>
                  {" · "}
                  <a
                    href="/impressum"
                    className="underline decoration-dotted underline-offset-2 hover:text-slate-300"
                    data-testid="banner-link-imprint"
                  >
                    {t("consent.link.imprint")}
                  </a>
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 flex-wrap justify-end">
              <Button
                variant="ghost"
                onClick={bannerCustomize}
                className="text-slate-300 hover:text-white hover:bg-[#272A35]"
                data-testid="banner-customize"
              >
                {t("consent.customize")}
              </Button>
              <Button
                variant="secondary"
                onClick={bannerRejectAll}
                className="bg-[#1A1E2C] border border-[#363A49] hover:bg-[#272A35] text-slate-100"
                data-testid="banner-reject-all"
              >
                {t("consent.rejectAll")}
              </Button>
              <Button
                onClick={bannerAcceptAll}
                className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold"
                data-testid="banner-accept-all"
              >
                {t("consent.acceptAll")}
              </Button>
            </div>
          </div>
        </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="bg-[#131620] border-[#272A35] text-slate-100 max-w-lg"
          data-testid="cookie-preferences-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl tracking-tight">
              {t("consent.dialog.title")}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm mt-1.5">
              {t("consent.dialog.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <CategoryRow
              icon={ShieldCheck}
              iconColor="text-emerald-400"
              title={t("consent.cat.necessary.title")}
              desc={t("consent.cat.necessary.desc")}
              enabled
              locked
              testId="cat-necessary"
            />
            <CategoryRow
              icon={Sparkles}
              iconColor="text-sky-400"
              title={t("consent.cat.functional.title")}
              desc={t("consent.cat.functional.desc")}
              enabled={draftFn}
              onChange={setDraftFn}
              testId="cat-functional"
            />
            <CategoryRow
              icon={BarChart3}
              iconColor="text-[#D4AF37]"
              title={t("consent.cat.statistics.title")}
              desc={t("consent.cat.statistics.desc")}
              enabled={draftSt}
              onChange={setDraftSt}
              testId="cat-statistics"
            />
          </div>

          <DialogFooter className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                rejectAll();
                setDialogOpen(false);
              }}
              className="bg-[#1A1E2C] border-[#363A49] hover:bg-[#272A35] text-slate-100"
              data-testid="dialog-reject-all"
            >
              {t("consent.rejectAll")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                acceptAll();
                setDialogOpen(false);
              }}
              className="bg-[#1A1E2C] border-[#363A49] hover:bg-[#272A35] text-slate-100"
              data-testid="dialog-accept-all"
            >
              {t("consent.acceptAll")}
            </Button>
            <Button
              onClick={dialogSave}
              className="bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold"
              data-testid="dialog-save"
            >
              {t("consent.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CategoryRow({
  icon: Icon,
  iconColor,
  title,
  desc,
  enabled,
  onChange,
  locked,
  testId,
}) {
  return (
    <div
      className="flex items-start gap-3 p-3 rounded-lg border border-[#272A35] bg-[#0C0E14]/60"
      data-testid={testId}
    >
      <div
        className={`h-8 w-8 rounded-md bg-[#131620] border border-[#272A35] flex items-center justify-center shrink-0 ${iconColor}`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-100">{title}</div>
        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{desc}</p>
      </div>
      <Switch
        checked={enabled}
        disabled={locked}
        onCheckedChange={onChange}
        data-testid={`${testId}-switch`}
      />
    </div>
  );
}
